import {
  CellKeyDownEvent,
  ColDef,
  FullWidthCellKeyDownEvent,
  RowDoubleClickedEvent,
} from '@ag-grid-community/core'

export enum ArrowEvents {
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  ArrowUp = 'ArrowUp',
  ArrowDown = 'ArrowDown',
}

/**
 * Add better keyboard shortcuts to the row groups.
 *
 * We are listening specifically to the arrow keys.
 * We stop propagation on the arrow keys.
 */
export function handleRowGroupCellKeyDown(
  event: FullWidthCellKeyDownEvent | CellKeyDownEvent,
) {
  const ev = event.event as KeyboardEvent
  if (!ev) {
    return
  }

  const codes = [
    ArrowEvents.ArrowDown,
    ArrowEvents.ArrowUp,
    ArrowEvents.ArrowLeft,
    ArrowEvents.ArrowRight,
  ]
  // Ignore other keys
  if (!codes.includes(ev.code as ArrowEvents)) {
    return
  }

  ev.stopPropagation()

  switch (ev.code) {
    case ArrowEvents.ArrowLeft:
      handleArrowLeft(event)
      break
    case ArrowEvents.ArrowRight:
      handleArrowRight(event)
      break
    case ArrowEvents.ArrowUp:
      handleArrowUp(event)
      break
    case ArrowEvents.ArrowDown:
      handleArrowDown(event)
      break
  }
}

/**
 * Double clicking should toggle the row group or dispatch the event if it's
 * a break row
 */
export function didHandleRowGroupDoubleClick(event: RowDoubleClickedEvent) {
  // Handle group double clicked
  if (typeof event.data.childCount === 'number') {
    event.node.setExpanded(!event.node.expanded)
    return true
  }

  return false
}

/**
 * If the node is a group, close the group.
 * If the node has a parent, close the parent group.
 */
function handleArrowLeft(event: FullWidthCellKeyDownEvent | CellKeyDownEvent) {
  if (event.node.group) {
    event.node.setExpanded(false)
    return
  }

  if (!event.node.parent) {
    return
  }

  const { parent } = event.node
  if (typeof parent.rowIndex !== 'number') {
    return
  }

  parent.setExpanded(false)
  parent.setSelected(true, true)
  event.api.setFocusedCell(
    parent.rowIndex,
    parent.rowGroupColumn?.getColId() ?? '',
  )
}

/**
 * Expand the group
 */
function handleArrowRight(event: FullWidthCellKeyDownEvent | CellKeyDownEvent) {
  event.node.setExpanded(true)
}

/**
 * Navigate up on the group
 */
function handleArrowUp(event: FullWidthCellKeyDownEvent | CellKeyDownEvent) {
  let rowIndex = (event.node.rowIndex ?? 0) - 1
  if (rowIndex < 0) {
    rowIndex = 0
  }
  const node = event.api.getDisplayedRowAtIndex(rowIndex)
  if (node) {
    node.setSelected(true, true)
  }
}

/**
 * Navigate down on the group
 */
function handleArrowDown(event: FullWidthCellKeyDownEvent | CellKeyDownEvent) {
  const rowIndex = (event.node.rowIndex ?? 0) + 1
  const node = event.api.getDisplayedRowAtIndex(rowIndex)
  if (!node) return

  node.setSelected(true, true)
  const cols: ColDef[] = event.api.getColumnDefs() ?? []

  if (cols.length === 0) return

  event.api.setFocusedCell(rowIndex, cols[0].colId ?? '')
}
